import React from 'react';
import { ChevronDown, Users, Briefcase, Sparkles, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { getTableTypeConfig } from '../../utils/tableUtils';

interface TypeDropdownProps {
  tableType: 'companies' | 'people' | 'custom';
  customType: string;
  showDropdown: boolean;
  disabled?: boolean;
  onToggleDropdown: () => void;
  onCloseDropdown: () => void;
  onTypeChange: (type: 'companies' | 'people' | 'custom') => void;
  onCustomTypeChange: (type: string) => void;
}

export const TypeDropdown: React.FC<TypeDropdownProps> = ({
  tableType,
  customType,
  showDropdown,
  disabled = false,
  onToggleDropdown,
  onCloseDropdown,
  onTypeChange,
  onCustomTypeChange,
}) => {
  return (
    <div className="relative w-full sm:w-auto">
      {tableType === 'custom' ? (
        <div
          className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 sm:py-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg hover:border-gray-300 transition-colors cursor-text ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
          onClick={() => !disabled && document.getElementById('custom-type-input')?.focus()}
          data-tour="type-selector-button"
        >
          <Sparkles className="hidden sm:block w-3 h-3 text-neutral-500 flex-shrink-0" />
          <input
            id="custom-type-input"
            type="text"
            value={customType}
            onChange={(e) => onCustomTypeChange(e.target.value)}
            placeholder="Type..."
            disabled={disabled}
            style={{ width: customType ? `${Math.max(4, customType.length) * 7 + 8}px` : '40px' }}
            className="min-w-[40px] max-w-[100px] bg-transparent text-xs font-medium text-neutral-600 placeholder:text-neutral-400 outline-none disabled:opacity-50"
          />
          {customType && !disabled && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCustomTypeChange('');
              }}
              className="p-0.5 text-neutral-400 hover:text-neutral-600 transition-colors flex-shrink-0"
            >
              <X className="w-3 h-3" />
            </button>
          )}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) onToggleDropdown();
            }}
            disabled={disabled}
            className="flex items-center text-neutral-400 hover:text-neutral-600 transition-colors flex-shrink-0 disabled:opacity-50"
          >
            <ChevronDown className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          onClick={onToggleDropdown}
          disabled={disabled}
          leftIcon={
            <span className="hidden sm:block">
              {tableType === 'companies' ? (
                <Briefcase className="w-3 h-3 text-neutral-500" />
              ) : (
                <Users className="w-3 h-3 text-neutral-500" />
              )}
            </span>
          }
          rightIcon={<ChevronDown className="w-3 h-3 text-neutral-400" />}
          className="capitalize justify-center w-full sm:w-auto"
          data-tour="type-selector-button"
        >
          {tableType}
        </Button>
      )}

      {showDropdown && !disabled && (
        <>
          <div className="fixed inset-0 z-10" onClick={onCloseDropdown} />
          <Card
            padding="none"
            shadow="md"
            className="absolute bottom-full left-0 mb-2 w-[220px] z-20 py-2 rounded-xl"
            data-tour="type-dropdown"
          >
            {(['companies', 'people', 'custom'] as const).map((type) => {
              const config = getTableTypeConfig(type);
              const Icon = config.icon;
              return (
                <button
                  key={type}
                  onClick={() => {
                    onTypeChange(type);
                    onCloseDropdown();
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-neutral-50 transition-colors ${
                    tableType === type ? 'bg-neutral-50' : ''
                  }`}
                >
                  <div
                    className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                      tableType === type ? 'bg-neutral-900' : 'bg-neutral-100'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${tableType === type ? 'text-white' : 'text-neutral-600'}`} />
                  </div>
                  <div>
                    <div className="text-[13px] font-medium text-neutral-900">{config.label}</div>
                    <div className="text-[11px] text-neutral-500">{config.subtitle}</div>
                  </div>
                </button>
              );
            })}
          </Card>
        </>
      )}
    </div>
  );
};
